import { FACEDOWN } from 'constants/gameConstants';
import Decimal from 'decimal.js';

import {
  CardRef,
  GameVariantId,
  HandHistorySnapshot,
  PotGroupPlayerSnapshot,
  SeatSnapshot
} from './types';

/** Hole cards dealt per variant; drives the face-down placeholders in a card header. */
export const holeCardCount = (gameVariant: GameVariantId): number =>
  gameVariant === 'omaha' ? 4 : 2;

const allPotPlayers = (hand: HandHistorySnapshot): PotGroupPlayerSnapshot[] => [
  ...hand.potGroups.flatMap((pot) => pot.players),
  ...(hand.pots ?? []).flatMap((pot) => pot.awards.flatMap((award) => award.players))
];

const hasCards = (cards?: CardRef[]): cards is CardRef[] =>
  Array.isArray(cards) && cards.length > 0 && cards.some((card) => card && card !== FACEDOWN);

/**
 * The viewer's hole cards for a completed hand, or face-down placeholders.
 *
 * The shared document never carries a player's own unshown cards; they arrive
 * later through own-card recovery and are merged into the hand by the context
 * (`revealedOwnCards`), so a card that starts face-down flips once recovery
 * lands. Observers (no viewer seat) get no cards at all.
 */
export const getViewerCards = (
  hand: HandHistorySnapshot
): { cards: CardRef[]; revealed: boolean } => {
  const viewerSeat = hand.tableSnapshot.viewerSeat;
  if (!viewerSeat) return { cards: [], revealed: false };

  const seat = hand.tableSnapshot.seats.find((candidate) => candidate.seat === viewerSeat);
  const seatCards = seat?.cards;
  if (seatCards && hasCards(seatCards)) return { cards: seatCards, revealed: true };

  for (const player of allPotPlayers(hand)) {
    if (player.seat !== viewerSeat) continue;
    const cards = player.cards;
    if (cards && hasCards(cards)) return { cards, revealed: true };
  }

  return {
    cards: Array.from({ length: holeCardCount(hand.gameVariant) }, () => FACEDOWN),
    revealed: false
  };
};

/**
 * The viewer's net for the hand. Prefers the server's consolidated result;
 * otherwise sums the viewer's per-pot nets (each award is an independent
 * settlement, so the sum is the hand total). The canonical `pots` when there
 * are any, else the legacy groups, never both: an Omaha capture emits
 * `potGroups` built from `pots` with the same player rows
 * (buildCompatibilityPotGroups), and a shared record never carries
 * `viewerResult` (the capture passes no viewer; only the playground's fixtures
 * have one), so every Omaha net, hi-lo included, summed both and came out
 * doubled. `undefined` when the viewer was not in the hand.
 */
export const getViewerNet = (hand: HandHistorySnapshot): number | undefined => {
  if (hand.viewerResult) return hand.viewerResult.net;

  const rows = viewerSettlementRows(hand);
  if (rows.length === 0) return undefined;

  return rows.reduce((sum, player) => sum.plus(player.net || 0), new Decimal(0)).toNumber();
};

/** The viewer's settlement rows, from `pots` or the legacy groups, never both (see getViewerNet). */
const viewerSettlementRows = (hand: HandHistorySnapshot): PotGroupPlayerSnapshot[] => {
  const viewerSeat = hand.tableSnapshot.viewerSeat;
  if (!viewerSeat) return [];

  const settlements = hand.pots?.length
    ? hand.pots.flatMap((pot) => pot.awards.flatMap((award) => award.players))
    : hand.potGroups.flatMap((pot) => pot.players);
  return settlements.filter((player) => player.seat === viewerSeat);
};

/**
 * Everything the viewer won in the hand: the sum of their winning rows'
 * `grossWin`, every pot, half and run — not the net of what they put in.
 * `undefined` when no winning row records a gross (the viewer won nothing, or
 * an older record that carries only nets).
 */
export const getViewerGrossWin = (hand: HandHistorySnapshot): number | undefined => {
  const wins = viewerSettlementRows(hand).filter(
    (player) => player.isWinner && typeof player.grossWin === 'number'
  );
  if (wins.length === 0) return undefined;

  return wins.reduce((sum, player) => sum.plus(player.grossWin ?? 0), new Decimal(0)).toNumber();
};

/** Total money in the hand: the canonical `pots` when present, else the legacy groups. */
export const getPotTotal = (hand: HandHistorySnapshot): number => {
  const pots: { amount: number }[] = hand.pots?.length ? hand.pots : hand.potGroups ?? [];
  return pots.reduce((sum, pot) => sum.plus(pot.amount || 0), new Decimal(0)).toNumber();
};

/**
 * The five cards that made the viewer's winning hand, when the viewer won this
 * hand and the capture recorded them; `undefined` otherwise. Drives the
 * dimming of the cards that did not play in the card header.
 */
export const getViewerWinningCards = (hand: HandHistorySnapshot): CardRef[] | undefined => {
  const viewerSeat = hand.tableSnapshot.viewerSeat;
  if (!viewerSeat) return undefined;
  const winningRow = allPotPlayers(hand).find(
    (player) =>
      player.seat === viewerSeat && player.isWinner && (player.winningCards?.length ?? 0) > 0
  );
  return winningRow?.winningCards;
};

/**
 * True when the pot went to the last player standing: nobody had to show, so
 * there is no "winning five" to light. The record's `winningCards` on such a
 * hand is whatever the engine last evaluated — on test1 it was the flop-time
 * best five on a hand that ended on the turn (Jason, 28 Sep) — so it is
 * ignored rather than trusted.
 */
export const isUncontestedWin = (hand: HandHistorySnapshot): boolean =>
  hand.tableSnapshot.seats.length > 0 &&
  hand.tableSnapshot.seats.filter((seat) => !seat.folded).length <= 1;

/** The hand with every `winningCards` removed, for a win nobody had to show for. */
export const withoutWinningCards = (hand: HandHistorySnapshot): HandHistorySnapshot => {
  const strip = <T extends { winningCards?: CardRef[] }>({ winningCards: _dropped, ...rest }: T) =>
    rest as T;
  return {
    ...hand,
    potGroups: hand.potGroups.map((pot) => ({ ...pot, players: pot.players.map(strip) })),
    ...(hand.pots
      ? {
          pots: hand.pots.map((pot) => ({
            ...pot,
            awards: pot.awards.map((award) => ({ ...award, players: award.players.map(strip) }))
          }))
        }
      : {})
  };
};

/** The boards of a hand: every runout for a multi-run hand, else the one board. */
export const getRunouts = (hand: HandHistorySnapshot): CardRef[][] =>
  hand.boards && hand.boards.length > 1 ? hand.boards : [hand.board];

/** What the record says about one runout for the viewer, and the five to light when it says so. */
export interface RunOutcome {
  result?: 'won' | 'lost';
  winningCards?: CardRef[];
}

/**
 * The viewer's result per runout, from the record (Jason, 28 Sep: a won run
 * keeps its five bright, a lost run reads as lost).
 *
 * One board: the viewer's winning five, as before, and no per-run result.
 *
 * Several boards: only the record's `run` tags say which run an award came
 * from. The cards cannot — runs that share a flop or turn each hold a five made
 * of shared cards, and the viewer's own cards are face-down until recovery
 * lands — so an untagged record claims nothing. A run is `won` when a winning
 * row of the viewer's is tagged to it, lit by that row's five when the record
 * has one (a won run without a five still reads won). A run is `lost` only when
 * every winning row in the hand is tagged, the viewer won another run, and this
 * run paid someone else; a partly tagged record never calls a run lost.
 */
export const getViewerRunOutcomes = (hand: HandHistorySnapshot): RunOutcome[] => {
  const runouts = getRunouts(hand);
  const viewerSeat = hand.tableSnapshot.viewerSeat;
  if (!viewerSeat || isUncontestedWin(hand)) return runouts.map(() => ({}));
  const winners = allPotPlayers(hand).filter((player) => player.isWinner);
  const fiveOf = (rows: PotGroupPlayerSnapshot[]) =>
    rows.find((player) => (player.winningCards?.length ?? 0) > 0)?.winningCards;
  if (runouts.length === 1) {
    return [{ winningCards: fiveOf(winners.filter((player) => player.seat === viewerSeat)) }];
  }

  const onBoard = (run?: number): run is number =>
    Number.isInteger(run) && (run as number) >= 1 && (run as number) <= runouts.length;
  const everyWinnerTagged = winners.length > 0 && winners.every((player) => onBoard(player.run));
  const viewerWonARun = winners.some((player) => player.seat === viewerSeat && onBoard(player.run));
  return runouts.map((_, index) => {
    const run = index + 1;
    const mine = winners.filter((player) => player.seat === viewerSeat && player.run === run);
    if (mine.length > 0) return { result: 'won', winningCards: fiveOf(mine) };
    const paidSomeoneElse = winners.some((player) => player.run === run);
    return everyWinnerTagged && viewerWonARun && paidSomeoneElse ? { result: 'lost' } : {};
  });
};

/**
 * The shared hand-history document can never carry a player's own unshown
 * cards (see ownCardsStore.js on the backend) - only genuinely-shown cards
 * ever reach this snapshot. `HandSnapshot` fetches the recovery backstop
 * automatically for whichever hand is currently open rather than for every
 * hand up front; this decides when that fetch is worth making.
 *
 * Deliberately checks for actual card data rather than trusting
 * `cardsRevealed` alone: an all-fold win can leave the shared snapshot with
 * `cardsRevealed: true` and `cards: []` at the same time (the observer
 * projection's mandatory-show branch decrypts the winner's cards, then its
 * own all-fold facedown sweep - "nobody outside the all-in family stays
 * revealed on an uncontested win" - sweeps them right back out, but whatever
 * sets `cardsRevealed` doesn't re-check that). Treat "no real cards" as
 * "worth trying to recover," regardless of what the flag claims.
 */
export const canRevealOwnCards = (
  viewerSeat: number | undefined,
  seats: SeatSnapshot[]
): boolean => {
  if (!viewerSeat) return false;
  const seat = seats.find((candidate) => candidate.seat === viewerSeat);
  if (!seat) return false;
  // Face-down placeholders are not cards: the same test getViewerCards uses.
  return !hasCards(seat.cards);
};
